import { useRef, useState } from "react";
import MainLayout from "Layouts/MainLayout/MainLayout";
import {
  useOpsHealth,
  useOpsLobby,
  useOpsDoctors,
  useOpsPrescriptions,
  useOpsInsights,
  useOpsAnalytics,
  useOpsUsersPatients,
  useOpsUsersProviders,
  useOpsStateCoverage,
  useOpsAlerts,
  useOpsAlertDetail,
} from "CustomHooks/useOperations";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import ClassNames from "Helpers/Common";
import "./Operations.css";
import { RANGES } from "./format";
import { TABS, DEFAULT_TAB, NO_RANGE_TABS } from "./constants";
import { TopProgressBar, TabSkeleton } from "./components";
import { AnalyticsTab } from "./tabs/AnalyticsTab";
import { SystemTab } from "./tabs/SystemTab";
import { PrescriptionsTab } from "./tabs/PrescriptionsTab";
import { PatientsTab } from "./tabs/PatientsTab";
import { DoctorsTab } from "./tabs/DoctorsTab";
import { AlertsTab } from "./tabs/AlertsTab";

// First-load skeleton shape per tab (cards / tables / charts).
const SKELETON = {
  analytics: { cards: 4, tables: 1 },
  patients: { cards: 0, tables: 1 },
  doctors: { cards: 0, tables: 2 },
  system: { cards: 3, tables: 1 },
  prescriptions: { cards: 4, tables: 2 },
  alerts: { cards: 4, tables: 0 },
};

export const OperationsDashboard = () => {
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [range, setRange] = useState("1");
  const [rxOpenPage, setRxOpenPage] = useState(1);
  const [rxResolvedPage, setRxResolvedPage] = useState(1);
  const [patientPage, setPatientPage] = useState(1);
  const [patientSort, setPatientSort] = useState({ key: "signedUp", order: "desc" });
  const [patientSearch, setPatientSearch] = useState("");
  const [providerPage, setProviderPage] = useState(1);
  const [providerSort, setProviderSort] = useState({ key: "signedUp", order: "desc" });
  const [providerSearch, setProviderSearch] = useState("");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertPage, setAlertPage] = useState(1);
  const forceRef = useRef(false);

  // clicking an alert card selects it and resets its list to page 1
  const selectAlert = (key) => { setSelectedAlert(key); setAlertPage(1); };

  // A search resets to page 1 so the first matches are visible.
  const searchPatients = (v) => { setPatientSearch(v); setPatientPage(1); };
  const searchProviders = (v) => { setProviderSearch(v); setProviderPage(1); };

  const changeRange = (r) => {
    setRange(r);
    setRxOpenPage(1);
    setRxResolvedPage(1);
    setPatientPage(1);
    setProviderPage(1);
  };

  // clicking a sortable header: toggle order if same key, else new key desc
  const toggleSort = (setSort, setPage) => (key) => {
    setPage(1);
    setSort((s) => (s.key === key ? { key, order: s.order === "asc" ? "desc" : "asc" } : { key, order: "desc" }));
  };

  // Analytics composes four endpoints: live (lobby + doctors, 30s poll) and
  // range-driven (analytics + insights). Prescriptions and System are single.
  const lobby = useOpsLobby({ range, enabled: tab === "analytics", forceRef });
  const doctors = useOpsDoctors({ range, enabled: tab === "analytics", forceRef });
  const analytics = useOpsAnalytics({ range, enabled: tab === "analytics", forceRef });
  const insights = useOpsInsights({ range, enabled: tab === "analytics", forceRef });
  const prescriptions = useOpsPrescriptions({
    range,
    page: rxOpenPage,
    resolvedPage: rxResolvedPage,
    enabled: tab === "prescriptions",
    forceRef,
  });
  const usersPatients = useOpsUsersPatients({
    range,
    page: patientPage,
    sort: patientSort,
    search: patientSearch,
    enabled: tab === "patients",
    forceRef,
  });
  const usersProviders = useOpsUsersProviders({
    range,
    page: providerPage,
    sort: providerSort,
    search: providerSearch,
    enabled: tab === "doctors",
    forceRef,
  });
  const stateCoverage = useOpsStateCoverage({ range, enabled: tab === "doctors", forceRef });
  const system = useOpsHealth({ enabled: tab === "system", forceRef });
  // Summary stays enabled across tabs so the Alerts tab badge is always current.
  const alerts = useOpsAlerts({ enabled: true, forceRef });
  const alertDetail = useOpsAlertDetail({
    key: selectedAlert,
    page: alertPage,
    enabled: tab === "alerts",
    forceRef,
  });

  // Primary query drives the tab's loading/error/skeleton; secondaries fill in.
  const PRIMARY = {
    analytics,
    patients: usersPatients,
    doctors: usersProviders,
    system,
    prescriptions,
    alerts,
  };
  const SECONDARY = {
    analytics: [lobby, doctors, insights],
    doctors: [stateCoverage],
    alerts: [alertDetail],
  };

  const primary = PRIMARY[tab];
  const secondaries = SECONDARY[tab] || [];
  const busy = primary.isFetching || secondaries.some((q) => q.isFetching);

  const handleRefresh = () => {
    forceRef.current = true;
    Promise.all([primary.refetch(), ...secondaries.map((q) => q.refetch())]).finally(() => {
      forceRef.current = false;
    });
  };

  const showRange = !NO_RANGE_TABS.includes(tab);

  return (
    <MainLayout>
      <div className="p-8 flex flex-col gap-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <span className="font-semibold text-2xl sapphire">Operations</span>
          <div className="flex items-center gap-4 flex-wrap">
            {showRange && (
              <div className="flex rounded-md border border-gray-300 overflow-hidden">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => changeRange(r.key)}
                    className={ClassNames(
                      "px-3 py-2 text-sm font-medium focus:outline-none",
                      range === r.key
                        ? "bg-blue-chill text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
            {primary.data?.checkedAt && (
              <span className="text-sm text-gray-400">
                Last checked {new Date(primary.data.checkedAt).toLocaleTimeString()}
                {primary.data.cached ? " (cached)" : ""}
              </span>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={busy}
              className={ClassNames(
                "px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-chill",
                busy ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
              )}
            >
              {busy ? "Checking..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={ClassNames(
                  "pb-3 text-sm font-medium border-b-2 focus:outline-none",
                  tab === t.key
                    ? "border-blue-chill sapphire"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                {t.label}
                {t.key === "alerts" && alerts.data?.total > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    {alerts.data.total}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* instant reaction to range change / refetch */}
        <TopProgressBar active={busy} />

        {primary.isError && (
          <div className="text-red-600 text-sm">
            {errorFormatter(primary.error?.response?.data?.errors || primary.error?.message)}
          </div>
        )}

        {primary.isLoading ? (
          <TabSkeleton {...SKELETON[tab]} />
        ) : (
          primary.data && (
            <div
              className={ClassNames(
                "flex flex-col gap-6 transition-opacity duration-200",
                busy ? "opacity-60" : "opacity-100"
              )}
            >
              {tab === "analytics" && (
                <AnalyticsTab
                  analytics={primary.data}
                  lobby={lobby.data}
                  doctors={doctors.data}
                  insights={insights.data}
                  range={range}
                />
              )}
              {tab === "patients" && (
                <PatientsTab
                  patients={usersPatients.data}
                  sort={patientSort}
                  onSort={toggleSort(setPatientSort, setPatientPage)}
                  onPage={setPatientPage}
                  onSearch={searchPatients}
                  loading={usersPatients.isFetching}
                />
              )}
              {tab === "doctors" && (
                <DoctorsTab
                  providers={usersProviders.data}
                  stateCoverage={stateCoverage.data}
                  sort={providerSort}
                  onSort={toggleSort(setProviderSort, setProviderPage)}
                  onPage={setProviderPage}
                  onSearch={searchProviders}
                  loading={usersProviders.isFetching}
                  loadingCoverage={stateCoverage.isFetching}
                  range={range}
                />
              )}
              {tab === "alerts" && (
                <AlertsTab
                  summary={primary.data}
                  detail={alertDetail.data}
                  selected={selectedAlert}
                  onSelect={selectAlert}
                  onPage={setAlertPage}
                  loadingDetail={alertDetail.isFetching}
                />
              )}
              {tab === "system" && <SystemTab data={primary.data} />}
              {tab === "prescriptions" && (
                <PrescriptionsTab
                  data={primary.data}
                  range={range}
                  setOpenPage={setRxOpenPage}
                  setResolvedPage={setRxResolvedPage}
                />
              )}
            </div>
          )
        )}
      </div>
    </MainLayout>
  );
};
